/**
 * Journey A — "Get your agency live" first-run setup checklist.
 *
 * A guided onboarding card pinned to the top of the dashboard for owners and
 * team leads. Each step shows a done ✓ or a "Do this →" link, with a progress
 * bar across the required steps (1–4). Step 5 (Connect Claude) is a bonus row
 * that can't be detected, so it never blocks completion.
 *
 * Done-state is derived from REAL data fetched on mount (reusing existing API
 * clients — no new backend calls):
 *   1. Connect your first brand  — `state.user.profile.all_shops.length >= 1`
 *   2. Connect ad platforms      — any MCP connection on the ACTIVE shop
 *                                  (`listMcpConnections(activeShopSlug)`).
 *                                  Checking every shop would be N requests;
 *                                  the active shop is the cheap proxy.
 *   3. Invite your team          — `listEmployees().length > 1`
 *   4. Set auto-approval rules   — `get_action_rules_api_call()` for the active
 *                                  shop returns >= 1 rule. (No agency-wide rules
 *                                  endpoint exists; the rules API is shop-scoped.)
 *   5. Connect Claude            — informational; always actionable.
 *
 * Once all REQUIRED steps (1–4) are done the card auto-hides, and a "dismissed"
 * flag is persisted to localStorage (scoped per user) so it stays hidden after
 * completion. The whole card is gated to owner / manager by the caller: the
 * Dashboard only renders it when `showOnboardingCard` is true, i.e.
 * `employeeRole === 'owner' || employeeRole === 'manager'` — only the roles
 * who onboard the org. (Note: this is an explicit role check, not a single
 * RoleGate capability, since `settings.manage_org` is owner-only.)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SimpleCard from '../../ui/cards/SimpleCard';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { RootState } from '../../store/store';
import {
  rocketSVG,
  shopSVG,
  chipSVG,
  twoUsersSVG,
  securitySVG,
  anthropicSVG,
  tickWithCircleSVG,
  arrowRightThinSVG,
} from '../../lib/allCustomSVGs';
import { listMcpConnections } from '../../lib/api/control-center/control-center-api';
import { listEmployees } from '../../lib/api/identity/identity-api';
import get_action_rules_api_call from '../../lib/api/actions/get-action-rules';
import '../../styles/pages/dashboard/getAgencyLiveCard.scss';

// localStorage key — scoped per user so a shared machine doesn't leak the
// dismissed flag across accounts.
const dismissedKey = (userKey: string) => `adup.onboarding.dismissed.${userKey}`;

interface StepConfig {
  id: string;
  icon: any;
  title: string;
  body: string;
  to: string;
  ctaKey: string;
  /** Bonus rows (Claude) don't count toward the progress bar / completion. */
  required: boolean;
}

const GetAgencyLiveCard = () => {
  const { t } = useTranslation();

  // ---- identity / data sources from Redux (cheap, already loaded) ----
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops ?? []
  );
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug ?? ''
  );
  // Stable per-user key for the dismissed flag.
  const userKey = useSelector(
    (s: RootState) =>
      s.user?.profile?.user_id ||
      s.user?.profile?.seller_id ||
      s.user?.profile?.email ||
      'anon'
  );
  // The MCP + employees clients authenticate with the agency Bearer token. If
  // the user never bridged into the agency portal there is nothing to query —
  // we skip those checks (steps stay "not done", which is the safe default).
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken ?? ''
  );
  // Guards use the boolean, but effects must key on the token VALUE: the
  // admin "login as seller" handoff swaps the agency session mid-page, and a
  // boolean dep would skip the refetch and keep stale wrong-org data.
  const hasAgencyToken = Boolean(agencyToken);

  // ---- async-derived done flags ----
  const hasBrand = (allShops?.length ?? 0) >= 1;
  const [hasPlatform, setHasPlatform] = useState<boolean>(false);
  const [hasTeam, setHasTeam] = useState<boolean>(false);
  const [hasRules, setHasRules] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // ---- dismissed flag (localStorage) ----
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(dismissedKey(userKey)) === 'true';
    } catch {
      return false;
    }
  });

  const persistDismissed = useCallback(() => {
    try {
      localStorage.setItem(dismissedKey(userKey), 'true');
    } catch {
      // ignore — storage may be unavailable (private mode); card simply
      // re-appears next mount, which is acceptable.
    }
    setDismissed(true);
  }, [userKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      // Run the three network probes in parallel. Each failure is swallowed
      // and treated as "not done" so a transient error never falsely marks a
      // step complete (or crashes the dashboard).
      const platformPromise: Promise<boolean> =
        hasAgencyToken && activeShopSlug
          ? listMcpConnections(activeShopSlug)
              .then((conns) => (conns?.length ?? 0) > 0)
              .catch(() => false)
          : Promise.resolve(false);

      const teamPromise: Promise<boolean> = hasAgencyToken
        ? listEmployees()
            .then((emps) => (emps?.length ?? 0) > 1)
            .catch(() => false)
        : Promise.resolve(false);

      const rulesPromise: Promise<boolean> = get_action_rules_api_call()
        .then((rules) => (rules?.length ?? 0) > 0)
        .catch(() => false);

      const [platform, team, rules] = await Promise.all([
        platformPromise,
        teamPromise,
        rulesPromise,
      ]);

      if (cancelled) return;
      setHasPlatform(platform);
      setHasTeam(team);
      setHasRules(rules);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [agencyToken, activeShopSlug]);

  const steps: StepConfig[] = useMemo(
    () => [
      {
        id: 'brand',
        icon: shopSVG,
        title: t('OnboardingStepBrandTitle'),
        body: t('OnboardingStepBrandBody'),
        to: '/clients',
        ctaKey: 'OnboardingDoThis',
        required: true,
      },
      {
        id: 'platform',
        icon: chipSVG,
        title: t('OnboardingStepPlatformTitle'),
        body: t('OnboardingStepPlatformBody'),
        to: '/control-center',
        ctaKey: 'OnboardingDoThis',
        required: true,
      },
      {
        id: 'team',
        icon: twoUsersSVG,
        title: t('OnboardingStepTeamTitle'),
        body: t('OnboardingStepTeamBody'),
        to: '/team',
        ctaKey: 'OnboardingDoThis',
        required: true,
      },
      {
        id: 'rules',
        icon: securitySVG,
        title: t('OnboardingStepRulesTitle'),
        body: t('OnboardingStepRulesBody'),
        to: '/actions/rules',
        ctaKey: 'OnboardingDoThis',
        required: true,
      },
      {
        id: 'claude',
        icon: anthropicSVG,
        title: t('OnboardingStepClaudeTitle'),
        body: t('OnboardingStepClaudeBody'),
        to: '/my-mcp-setup',
        ctaKey: 'OnboardingConnect',
        required: false,
      },
    ],
    [t]
  );

  const doneById: Record<string, boolean> = {
    brand: hasBrand,
    platform: hasPlatform,
    team: hasTeam,
    rules: hasRules,
    // Claude can't be detected; never shown as done so the bonus CTA stays.
    claude: false,
  };

  const requiredSteps = steps.filter((s) => s.required);
  const completedCount = requiredSteps.filter((s) => doneById[s.id]).length;
  const totalRequired = requiredSteps.length;
  const allRequiredDone = completedCount === totalRequired;
  const progressPct = totalRequired
    ? Math.round((completedCount / totalRequired) * 100)
    : 0;

  // Auto-hide once everything required is done (and persist so it stays gone).
  useEffect(() => {
    if (!loading && allRequiredDone && !dismissed) {
      persistDismissed();
    }
  }, [loading, allRequiredDone, dismissed, persistDismissed]);

  // Don't render while we don't yet know the state (avoids a flash of an
  // already-complete card), or once dismissed / fully complete.
  if (dismissed || (allRequiredDone && !loading)) return null;

  return (
    <SimpleCard grow={false}>
      <div className='getAgencyLiveCard'>
        <div className='getAgencyLiveCard__header'>
          <div className='getAgencyLiveCard__header-titleRow'>
            <span className='getAgencyLiveCard__header-icon' aria-hidden='true'>
              <CustomSVGs svg={rocketSVG} />
            </span>
            <div>
              <h3 className='getAgencyLiveCard__header-title'>
                {t('OnboardingCardTitle')}
              </h3>
              <p className='getAgencyLiveCard__header-subtitle'>
                {t('OnboardingCardSubtitle')}
              </p>
            </div>
          </div>
          <button
            type='button'
            className='getAgencyLiveCard__dismiss'
            onClick={persistDismissed}
            aria-label={t('OnboardingDismiss')}
            title={t('OnboardingDismiss')}
          >
            ×
          </button>
        </div>

        <div className='getAgencyLiveCard__progress'>
          <div className='getAgencyLiveCard__progress-track'>
            <div
              className='getAgencyLiveCard__progress-fill'
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className='getAgencyLiveCard__progress-label'>
            {t('OnboardingProgress', {
              done: completedCount,
              total: totalRequired,
            })}
          </span>
        </div>

        <ul className='getAgencyLiveCard__steps'>
          {steps.map((step) => {
            const isDone = doneById[step.id];
            return (
              <li
                key={step.id}
                className={`getAgencyLiveStep${
                  isDone ? ' getAgencyLiveStep--done' : ''
                }${step.required ? '' : ' getAgencyLiveStep--bonus'}`}
              >
                <span
                  className={`getAgencyLiveStep__status${
                    isDone ? ' getAgencyLiveStep__status--done' : ''
                  }`}
                  aria-hidden='true'
                >
                  {isDone ? (
                    <CustomSVGs svg={tickWithCircleSVG} />
                  ) : (
                    <CustomSVGs svg={step.icon} />
                  )}
                </span>
                <div className='getAgencyLiveStep__text'>
                  <span className='getAgencyLiveStep__title'>
                    {step.title}
                    {!step.required && (
                      <span className='getAgencyLiveStep__badge'>
                        {t('OnboardingBonus')}
                      </span>
                    )}
                  </span>
                  <span className='getAgencyLiveStep__body'>{step.body}</span>
                </div>
                <div className='getAgencyLiveStep__action'>
                  {isDone ? (
                    <span className='getAgencyLiveStep__doneLabel'>
                      {t('OnboardingDone')}
                    </span>
                  ) : (
                    <Link
                      to={step.to}
                      className='getAgencyLiveStep__cta'
                    >
                      {t(step.ctaKey)}
                      <CustomSVGs svg={arrowRightThinSVG} />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </SimpleCard>
  );
};

export default GetAgencyLiveCard;
